import React, { useState, createContext } from "react";
import { BikeItemContent } from "../pages/sykler/bestill/[bikeId]";
import { BikeModelsQ } from "/lib/sanity/queries/bikeModels";

const cartState = {
  bike: {
    id: "",
    title: "",
    price: 0,
    image: {},
    products: [],
    subscriptions: []
  },
  subscription: { price: 0, title: "", id: "" },
  accessories: [],
  form: {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    address: "",
    zipCode: "",
    message: ""
  },
  stripeFormClientSecret: ""
};

const selectedBikeState = {
  id: "",
  bike: { id: "", title: "", price: 0, image: {}, products: [] },
  subscriptions: [{ id: "", price: 0, name: "" }],
  accessories: [{ id: "", price: 0, name: "", image: {}, quantity: 0 }]
};

export type StateType = {
  bike: BikeItemContent;
  subscription: {
    id: string;
    title: string;
    price: number;
  };
  accessories: {
    title: string;
    id: string;
    price: number;
    quantity: number;
    image: { asset: any } | BikeModelsQ[0]["image"];
  }[];

  form: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    address: string;
    zipCode: string;
    message: string;
  };

  stripeFormClientSecret: string;
};

type State = {
  state: StateType;
  selectedBike: BikeItemContent;
  total: number;
  addBike: (selectedBikeState: BikeItemContent) => void;
  addSubscription: (selectedBikeState: { id: string; price: number; title: string }) => void;
  addAccessory: (accessory: {
    id: string;
    price: number;
    title: string;
    image: { asset: any };
  }) => void;
  removeAccessory: (selectedBikeState: string) => void;
  removeAccessories: () => void;
  updateForm: (data: { key: string; value: string }) => void;
  setStripeFormClientSecret: (secret: string) => void;
  addAccessories: (bundle: string[]) => void;
};

const CartContext = createContext<State>({
  addBike(): void {},
  addSubscription(): void {},
  addAccessory(): void {},
  removeAccessory(): void {},
  updateForm(): void {},
  setStripeFormClientSecret(): void {},
  // @ts-ignore
  state: cartState,
  // @ts-ignore
  selectedBike: selectedBikeState,
  total: 0
});

export const CartProvider = ({ children }: { children: any }) => {
  // @ts-ignore
  const [state, setState] = useState<StateType>(cartState);
  // Use this to render current bike order selection and preserve the state when user navigate  next/back steps
  // @ts-ignore
  const [selectedBike, setSelectedBike] = useState<BikeItemContent>(selectedBikeState);

  const subscriptionPrice = state?.subscription?.price || 0;

  const accessoriesPrice = state?.accessories?.reduce(
    (total: number, current) => total + current.price * current.quantity,
    0
  );

  const total = subscriptionPrice + accessoriesPrice;

  const addBike = (bike: BikeItemContent) => {
    // Duplicate state since we are mutating
    setSelectedBike(bike);

    const newState: any = { ...state };

    // bike don't exist in state
    if (state.bike?.bike?.id !== bike.bike.id) {
      // Add new bike
      newState.bike = bike;
      // Reset subscription and accessories state
      newState.subscription = {};
      newState.accessories = [];
    }

    // Set state
    setState(newState);
  };

  const addSubscription = (subscription: { id: string; price: number; title: string }) => {
    // Duplicate state since we are mutating
    const newState = { ...state };
    // Update existing subscription
    newState.subscription = subscription;

    // Set state
    setState(newState);
  };

  const addAccessory = (accessory: {
    title: string;
    id: any;
    price: number;
    image: { asset: any };
  }) => {
    // Check if product exist in cart
    const index = state.accessories.findIndex((product) => product.id === accessory.id);

    // Duplicate state since we are mutating
    const newState = { ...state };

    // Update existing
    if (index > -1) {
      newState.accessories[index].quantity += 1;
    } else {
      // Add new product
      newState.accessories.push({
        ...accessory,
        quantity: 1
      });
    }

    // Set state
    setState(newState);
  };

  const addAccessories = (accessoryIds: string[]) => {
    // Get all details related to the accessories from the state. Because thats the way it is now.
    const accessoryObjects = state.bike.cms.products.filter((acc) => accessoryIds.includes(acc.id));

    const tmp = { ...state };

    // When adding a bundle we clear all accessories.
    tmp.accessories = [];

    // Adds all accessories in bundle to tmp object
    for (const accessory of accessoryObjects) {
      const { title, id, price, image } = accessory;

      tmp.accessories.push({ title, id, price, image, quantity: 1 });
    }

    // Sets cart to be tmp
    setState(tmp);
  };

  const removeAccessories = () => {
    const tmp = { ...state };
    tmp.accessories = [];
    setState(tmp);
  };

  const removeAccessory = (id: string) => {
    const index = state.accessories.findIndex((product) => product.id === id);

    const newState = { ...state };

    if (index > -1) {
      if (newState.accessories[index].quantity === 1) {
        // If only one item left, remove item completely
        newState.accessories.splice(index, 1);
      } else {
        // Decrease quantity
        newState.accessories[index].quantity -= 1;
      }
    }

    setState(newState);
  };

  const updateForm = ({ key, value }: { key: string; value: string }) => {
    const newState = { ...state };

    // @ts-ignore
    newState.form[key] = value;

    setState(newState);
  };

  const setStripeFormClientSecret = (clientSecret: string) => {
    const newState = { ...state };

    newState.stripeFormClientSecret = clientSecret;

    setState(newState);
  };

  return (
    <CartContext.Provider
      value={{
        state,
        selectedBike,
        total,
        addBike,
        addSubscription,
        addAccessory: addAccessory,
        addAccessories: addAccessories,
        removeAccessory,
        removeAccessories,
        updateForm,
        setStripeFormClientSecret
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
